import { QueryClient, QueryKey } from 'react-query';

/**
 * The most recently updated cached value for a connection-scoped query,
 * ignoring the connection-token segment of its key.
 *
 * These queries key on the connection token (VET-6426) so they re-run when a
 * connection is re-authenticated. Feeding this to `placeholderData` turns that
 * key change into a refetch rather than a first load, so consumers never drop
 * back to a loading state while the new token's request is in flight.
 *
 * `connectionKeyPrefix` must identify a single connection and exclude the token
 * segment — that scoping is what keeps a connection *switch* from showing the
 * previous connection's data. Unlike `keepPreviousData`, which carries data
 * across any key change, this can only ever return data for one connection.
 *
 * Read-only: `placeholderData` is never written to the cache, so the new key
 * still fetches and stores its own value.
 */
export const previousConnectionData = <T>(
  queryClient: QueryClient,
  connectionKeyPrefix: QueryKey
): T | undefined => {
  const [freshest] = queryClient
    .getQueryCache()
    .findAll(connectionKeyPrefix)
    .filter((query) => query.state.data !== undefined)
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);

  return freshest?.state.data as T | undefined;
};
